import React, { useState, useEffect } from 'react';
import { Video, Film, Loader2, ChevronDown, ChevronRight, WifiOff } from 'lucide-react';
import EventDetailsCell from './EventDetailsCell';

// Site name mapping based on site codes (positions 5-7 of account number)
const siteCodeMapping = {
    "001": "Songloed",
    "002": "Zandput", 
    "003": "Eerstegoud",
    "004": "Knobel",
    "005": "Soekmekaar",
    "006": "Singo",
    "007": "Hlanganani",
    "008": "Rebolwa",
    "009": "Venulu",
    "010": "Thomo",
    "011": "Malamulele",
    "012": "Muledani",
    "013": "Shayandima",
    "014": "Tshikweta",
    "015": "Makonde",
    "016": "Sanari",
    "017": "Pondrift",
    "018": "Silica",
    "019": "Polokwane",
    "020": "Louis Trichardt"
};

// Helper function to get site name from account number by extracting site code
const getSiteName = (accountNumber) => {
  if (!accountNumber || accountNumber.length < 7) return null;
  
  // Extract site code (positions 4-6, 0-indexed) from account number
  const siteCode = accountNumber.substring(4, 7);
  return siteCodeMapping[siteCode] || null;
};

// Custom date formatter function
const formatDate = (dateString) => {
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString();
  } catch (error) {
    return dateString;
  }
};

// Custom time formatter function
const formatTime = (dateString) => {
  try {
    const date = new Date(dateString);
    return date.toLocaleTimeString();
  } catch (error) {
    return '';
  }
};

export const ClusteredEventsTable = ({ 
  events, 
  filterStatus, 
  onAcknowledge, 
  isLoading, 
  onViewVideo, 
  onDownloadVideo,
  panelStatuses,
  isPanelOnline
}) => {
  const [clusteredEvents, setClusteredEvents] = useState([]);
  const [expandedClusters, setExpandedClusters] = useState(new Set());
  
  // Updated clustering logic with chronological sorting
  useEffect(() => {
    if (!events || events.length === 0) return;
    
    const clusters = [];
    const processedIds = new Set();
    
    // First sort events by timestamp, newest first
    const sortedEvents = [...events].sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
    
    // First pass: process all HIGH, MEDIUM, WARNING events as primary events
    sortedEvents.forEach(event => {
      // Skip if we've already processed this event
      if (processedIds.has(event._id)) return;
      
      // Filter based on status
      const matchesFilter = 
        filterStatus === 'ALL' || 
        (filterStatus === 'NEEDS_REVIEW' && event.alarmStatus === 'PENDING') ||
        (filterStatus === 'CONFIRMED' && event.alarmStatus === 'CONFIRMED') ||
        (filterStatus === 'FALSE' && event.alarmStatus === 'FALSE') ||
        (filterStatus === 'DEFAULT' && event.alarmStatus === 'DEFAULT');
      
      if (!matchesFilter) return;
      
      // Skip INFO events in the first pass
      const eventSeverity = event.severity || 'UNKNOWN';
      if (eventSeverity === 'INFO') return;
      
      // Now we're only processing HIGH, MEDIUM, WARNING events as primary
      const eventTime = new Date(event.timestamp);
      const eventAccount = event.accountNumber || '';
      const eventDeviceId = event.deviceId || '';
      
      // Find related INFO events within 1 minute (60 seconds) with same device or account
      const relatedEvents = sortedEvents.filter(e => {
        if (e._id === event._id || processedIds.has(e._id)) return false;
        
        // Only include INFO level events as related events
        const eSeverity = e.severity || 'UNKNOWN';
        if (eSeverity !== 'INFO') return false;
        
        const eTime = new Date(e.timestamp);
        const timeDiff = Math.abs(eTime - eventTime) / 1000; // difference in seconds
        const sameDevice = (e.deviceId || '') === eventDeviceId && eventDeviceId !== '';
        const sameAccount = (e.accountNumber || '') === eventAccount && eventAccount !== '';
        
        // For HIGH severity, use 60 second window
        if (eventSeverity === 'HIGH') {
          return (sameDevice || sameAccount) && timeDiff <= 60; // 1 minute
        } 
        // For MEDIUM and WARNING, use 30 second window
        else {
          return (sameDevice || sameAccount) && timeDiff <= 30; // 30 seconds
        }
      });
      
      // Mark all found events as processed
      processedIds.add(event._id);
      relatedEvents.forEach(e => processedIds.add(e._id));
      
      // Sort the related events chronologically (oldest first)
      relatedEvents.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
      
      // Add the cluster
      clusters.push({
        id: `cluster-${event._id}`,
        primaryEvent: event,
        relatedEvents: relatedEvents,
        allEvents: [event, ...relatedEvents]
      });
    });
    
    // Second pass: process remaining INFO events as standalone
    sortedEvents.forEach(event => {
      // Skip if we've already processed this event
      if (processedIds.has(event._id)) return;
      
      // Filter based on status
      const matchesFilter = 
        filterStatus === 'ALL' || 
        (filterStatus === 'NEEDS_REVIEW' && event.alarmStatus === 'PENDING') ||
        (filterStatus === 'CONFIRMED' && event.alarmStatus === 'CONFIRMED') ||
        (filterStatus === 'FALSE' && event.alarmStatus === 'FALSE') ||
        (filterStatus === 'DEFAULT' && event.alarmStatus === 'DEFAULT');
      
      if (!matchesFilter) return;
      
      // At this point, we're only dealing with INFO events
      // Mark as processed
      processedIds.add(event._id);
      
      // Add as standalone cluster
      clusters.push({
        id: `cluster-${event._id}`,
        primaryEvent: event,
        relatedEvents: [],
        allEvents: [event]
      });
    });
    
    // ===== UPDATED SORTING LOGIC =====
    // Sort all clusters by timestamp (newest first) 
    clusters.sort((a, b) => {
      // Get timestamps for comparison
      const aTime = new Date(a.primaryEvent.timestamp);
      const bTime = new Date(b.primaryEvent.timestamp);
      
      // For events with the same timestamp, prioritize by severity
      if (aTime.getTime() === bTime.getTime()) {
        const getSeverityRank = (severity) => {
          switch(severity) {
            case 'HIGH': return 0;
            case 'MEDIUM': return 1;
            case 'WARNING': return 2;
            case 'INFO': return 3;
            default: return 4;
          }
        };
        
        const aRank = getSeverityRank(a.primaryEvent.severity || 'UNKNOWN');
        const bRank = getSeverityRank(b.primaryEvent.severity || 'UNKNOWN');
        
        return aRank - bRank;
      }
      
      // Otherwise sort by timestamp (newest first)
      return bTime - aTime;
    });
    
    setClusteredEvents(clusters);
  }, [events, filterStatus]);

  const toggleExpanded = (clusterId) => {
    setExpandedClusters(prev => {
      const newSet = new Set(prev);
      if (newSet.has(clusterId)) {
        newSet.delete(clusterId);
      } else {
        newSet.add(clusterId);
      }
      return newSet;
    });
  };

  const getRowStyle = (event) => {
    switch (event.alarmStatus) {
      case 'CONFIRMED':
        return 'bg-red-50';
      case 'FALSE':
        return 'bg-green-50';
      case 'PENDING':
        return 'bg-amber-50';
      case 'DEFAULT':
      default:
        return 'bg-white';
    }
  };

  const getStatusStyle = (event) => {
    switch (event.alarmStatus) {
      case 'CONFIRMED':
        return 'bg-red-100 text-red-800 border border-red-200';
      case 'FALSE':
        return 'bg-green-100 text-green-800 border border-green-200';
      case 'PENDING':
        return 'bg-amber-100 text-amber-800 border border-amber-200';
      case 'DEFAULT':
      default:
        return 'bg-gray-100 text-gray-800 border border-gray-200';
    }
  };

  const getStatusText = (event) => {
    switch (event.alarmStatus) {
      case 'CONFIRMED':
        return 'Confirmed Alarm';
      case 'FALSE':
        return 'False Alarm';
      case 'PENDING':
        return 'Pending Review';
      case 'DEFAULT':
      default:
        return 'Default';
    }
  };

  // Check if event needs acknowledgment (only HIGH severity events with PENDING status)
  const needsAcknowledgment = (event) => {
    return event.severity === 'HIGH' && event.alarmStatus === 'PENDING';
  };

  // Check if event is a VIDEOAVAILABLE event
  const isVideoAvailableEvent = (event) => {
    return event.message && event.message.includes('VIDEOAVAILABLE');
  };

  // Check if event needs a download button
  const needsDownloadButton = (event, allEvents) => {
    // Check if event is a VIDEOAVAILABLE event with READY status
    if (isVideoAvailableEvent(event) && 
        event.videoDetails && 
        event.videoDetails.status === 'READY') {
      
      
      return false;
    }
    
    return false;
  };

  // Check if download is complete
  const isDownloadComplete = (event) => {
    return isVideoAvailableEvent(event) && 
           event.videoDetails && 
           event.videoDetails.status === 'COMPLETE';
  };


  if (!events || events.length === 0) {
    return <div className="text-center py-8">No events found</div>;
  }

  return (
    <div className="bg-white shadow-lg overflow-hidden pt-16">
      {/* Fixed Header */}
      <div className="fixed top-28 left-0 right-0 bg-gray-50 border-y z-40">
        <div className="max-w-full xl:max-w-7xl mx-auto">
          <table className="min-w-full table-fixed">
            <thead>
              <tr>
                <th className="w-6 p-[30px]"></th>
                <th className="w-32 lg:w-36 p-[30px] text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date/Time
                </th>
                <th className="w-36 lg:w-40 p-[30px] text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Device & Account
                </th>
                <th style={{ width: '338.47px' }} className="p-[30px] text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Event Information
                </th>
                <th className="w-40 lg:w-44 p-[30px] text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="w-44 lg:w-48 p-[30px] text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
          </table>
        </div>
      </div>
  
      {/* Table Body */}
      <div className="max-w-full xl:max-w-7xl mx-auto">
        <table className="min-w-full table-fixed divide-y divide-gray-200">
          <tbody className="divide-y divide-gray-200">
            {clusteredEvents.map((cluster) => (
              <React.Fragment key={cluster.id}>
                <tr className={`${getRowStyle(cluster.primaryEvent)} transition-colors duration-150`}>
                  <td className="w-6 p-[30px] cursor-pointer" onClick={() => toggleExpanded(cluster.id)}>
                    <div className="flex items-center justify-center">
                      {cluster.relatedEvents.length > 0 && (
                        expandedClusters.has(cluster.id) ? 
                          <ChevronDown className="h-4 w-4 text-gray-500" /> : 
                          <ChevronRight className="h-4 w-4 text-gray-500" />
                      )}
                    </div>
                  </td>
                  
                  <td className="w-32 lg:w-36 p-[30px]">
                    <div className="text-sm font-medium text-gray-900">
                      {formatDate(cluster.primaryEvent.timestamp)}
                    </div>
                    <div className="text-sm text-gray-500">
                      {formatTime(cluster.primaryEvent.timestamp)}
                    </div>
                    {cluster.relatedEvents.length > 0 && (
                      <div className="mt-1 text-xs text-gray-500 bg-gray-100 px-1 py-0.5 rounded">
                        +{cluster.relatedEvents.length} related event{cluster.relatedEvents.length > 1 ? 's' : ''}
                      </div>
                    )}
                  </td>

                  <td className="w-36 lg:w-40 p-[30px]">
                    <div className="text-sm font-medium text-gray-900">
                      {cluster.primaryEvent.deviceId || 'N/A'}
                    </div>
                    {cluster.primaryEvent.accountNumber && (
                      <div className="text-sm text-gray-500">
                        Acc-Number: {cluster.primaryEvent.accountNumber}
                      </div>
                    )}
                    {getSiteName(cluster.primaryEvent.accountNumber) && (
                      <div className="text-xs px-2 py-1 rounded font-medium bg-blue-50 text-blue-700 border border-blue-100 inline-block mt-1">
                        {getSiteName(cluster.primaryEvent.accountNumber)}
                      </div>
                    )}
                    {/* Display panel offline indicator if applicable */}
                    {cluster.primaryEvent.locationCode && !isPanelOnline(cluster.primaryEvent.locationCode) && (
                      <div className="mt-1 flex items-center text-xs text-red-500 bg-red-50 px-2 py-1 rounded border border-red-200">
                        <WifiOff className="h-3 w-3 mr-1" /> Offline
                      </div>
                    )}
                  </td>

                  <EventDetailsCell event={cluster.primaryEvent} />

                  <td className="w-40 lg:w-44 p-[30px]">
                    <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold ${getStatusStyle(cluster.primaryEvent)}`}>
                      {getStatusText(cluster.primaryEvent)}
                    </span>
                    {(cluster.primaryEvent.statusComment || cluster.primaryEvent.acknowledgedBy) && (
                      <div className="mt-1 text-sm text-gray-500 italic break-words">
                        {cluster.primaryEvent.statusComment && `"${cluster.primaryEvent.statusComment}"`}
                        {cluster.primaryEvent.acknowledgedBy && (
                          <div className="text-xs text-gray-400 mt-1">
                            Acknowledged by: {cluster.primaryEvent.acknowledgedBy}
                          </div>
                        )}
                      </div>
                    )}
                  </td>

                  <td className="w-44 lg:w-48 p-[30px]">
                    <div className="flex flex-col space-y-2">
                      {needsAcknowledgment(cluster.primaryEvent) && (
                        <button
                          onClick={(e) => onAcknowledge(cluster.primaryEvent, e)}
                          disabled={isLoading}
                          className={`${
                            isLoading 
                              ? 'bg-gray-400 cursor-not-allowed'
                              : 'bg-blue-500 hover:bg-blue-600'
                          } text-white px-4 py-2 text-sm transition-colors duration-200`}
                        >
                          {isLoading ? 'Processing...' : 'Acknowledge'}
                        </button>
                      )}
                      
                      {/* Video viewing buttons for primary event */}
                      {/* Only show video button section if videos are available or being received */}
                      {(cluster.primaryEvent.videoDetails?.previewVideo?.data === 'VIDEO-CLIP GENERATED' || 
                        cluster.allEvents.some(e => e.videoDetails?.previewVideo?.data === 'VIDEO-CLIP GENERATED') ||
                        cluster.primaryEvent.videoDetails?.fullVideo?.data || 
                        cluster.allEvents.some(e => e.videoDetails?.fullVideo?.data) ||
                        cluster.allEvents.some(e => e.videoDetails?.status === 'RECEIVING')) && (
                        <div className="grid grid-cols-2 gap-2">
                          {/* Clip button - only show if available or receiving */}
                          {cluster.primaryEvent.videoDetails?.previewVideo?.data === 'VIDEO-CLIP GENERATED' || 
                           cluster.allEvents.some(e => e.videoDetails?.previewVideo?.data === 'VIDEO-CLIP GENERATED') ? (
                            <button
                              onClick={() => onViewVideo(
                                cluster.primaryEvent.videoDetails?.previewVideo?.data === 'VIDEO-CLIP GENERATED' 
                                  ? cluster.primaryEvent 
                                  : cluster.allEvents.find(e => e.videoDetails?.previewVideo?.data === 'VIDEO-CLIP GENERATED'),
                                'preview'
                              )}
                              className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 text-sm transition-colors duration-200 flex items-center justify-center"
                            >
                              <Video className="w-4 h-4 mr-1" />
                              Clip
                            </button>
                          ) : cluster.allEvents.some(e => e.videoDetails?.status === 'RECEIVING') ? (
                            <div className="bg-gray-50 text-gray-400 px-4 py-2 text-sm flex items-center justify-center">
                              <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                              Clip
                            </div>
                          ) : null}
                          
                          {/* Full button - only show if available or receiving */}
                          {cluster.primaryEvent.videoDetails?.fullVideo?.data || 
                           cluster.allEvents.some(e => e.videoDetails?.fullVideo?.data) ? (
                            <button
                              onClick={() => onViewVideo(
                                cluster.primaryEvent.videoDetails?.fullVideo?.data 
                                  ? cluster.primaryEvent 
                                  : cluster.allEvents.find(e => e.videoDetails?.fullVideo?.data),
                                'full'
                              )}
                              className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 text-sm transition-colors duration-200 flex items-center justify-center"
                            >
                              <Film className="w-4 h-4 mr-1" />
                              Full
                            </button>
                          ) : cluster.allEvents.some(e => e.videoDetails?.status === 'RECEIVING') ? (
                            <div className="bg-gray-50 text-gray-400 px-4 py-2 text-sm flex items-center justify-center">
                              <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                              Full
                            </div>
                          ) : null}
                        </div>
                      )}
                      
                      {needsDownloadButton(cluster.primaryEvent, events) && (
                        <button
                          onClick={() => onDownloadVideo(cluster.primaryEvent)}
                          disabled={isLoading || (cluster.primaryEvent.locationCode && !isPanelOnline(cluster.primaryEvent.locationCode))}
                          className={`${
                            isLoading || (cluster.primaryEvent.locationCode && !isPanelOnline(cluster.primaryEvent.locationCode))
                              ? 'bg-gray-400 cursor-not-allowed'
                              : 'bg-purple-500 hover:bg-purple-600'
                          } text-white px-4 py-2 text-sm transition-colors duration-200 flex items-center justify-center`}
                        >
                          {cluster.primaryEvent.locationCode && !isPanelOnline(cluster.primaryEvent.locationCode) ? (
                            <>
                              <WifiOff className="h-4 w-4 mr-1" /> Offline
                            </>
                          ) : (
                            isLoading ? 'Processing...' : 'Download'
                          )}
                        </button>
                      )}
                      {isDownloadComplete(cluster.primaryEvent) && (
                        <button
                          disabled={true}
                          className="bg-green-500 text-white px-4 py-2 text-sm cursor-default"
                        >
                          Complete
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
                
                {/* Expanded related events */}
                {expandedClusters.has(cluster.id) && cluster.relatedEvents.length > 0 && (
                  <tr className="bg-gray-50">
                    <td colSpan="6" className="p-0">
                      <div className="pl-8">
                        <table className="min-w-full table-fixed">
                          <tbody className="divide-y divide-gray-200">
                            {/* Note: relatedEvents are already sorted chronologically in the useEffect hook */}
                            {cluster.relatedEvents.map((relatedEvent) => (
                              <tr key={relatedEvent._id} className="border-t border-gray-200">
                                <td className="w-6 p-[30px]">
                                  {/* Empty cell for indentation */}
                                </td>
                                
                                <td className="w-32 lg:w-36 p-[30px]">
                                  <div className="text-sm font-medium text-gray-900">
                                    {formatDate(relatedEvent.timestamp)}
                                  </div>
                                  <div className="text-sm text-gray-500">
                                    {formatTime(relatedEvent.timestamp)}
                                  </div>
                                </td>

                                <td className="w-36 lg:w-40 p-[30px]">
                                  <div className="text-sm font-medium text-gray-900">
                                    {relatedEvent.deviceId || 'N/A'}
                                  </div>
                                  {relatedEvent.accountNumber && (
                                    <div className="text-sm text-gray-500">
                                      Acc-Number: {relatedEvent.accountNumber}
                                    </div>
                                  )}
                                  {getSiteName(relatedEvent.accountNumber) && (
                                    <div className="text-xs px-2 py-1 rounded font-medium bg-blue-50 text-blue-700 border border-blue-100 inline-block mt-1">
                                      Site: {getSiteName(relatedEvent.accountNumber)}
                                    </div>
                                  )}
                                  {/* Display panel offline indicator for related events if applicable */}
                                  {relatedEvent.locationCode && !isPanelOnline(relatedEvent.locationCode) && (
                                    <div className="mt-1 flex items-center text-xs text-red-500 bg-red-50 px-2 py-1 rounded border border-red-200">
                                      <WifiOff className="h-3 w-3 mr-1" /> Offline
                                    </div>
                                  )}
                                </td>

                                <EventDetailsCell event={relatedEvent} />

                                <td className="w-40 lg:w-44 p-[30px]">
                                  {/* Status cell is now empty for sub-events */}
                                </td>

                                <td className="w-44 lg:w-48 p-[30px]">
                                  <div className="flex flex-col space-y-2">
                                    {needsDownloadButton(relatedEvent, events) && (
                                      <button
                                        onClick={() => onDownloadVideo(relatedEvent)}
                                        disabled={isLoading || (relatedEvent.locationCode && !isPanelOnline(relatedEvent.locationCode))}
                                        className={`${
                                          isLoading || (relatedEvent.locationCode && !isPanelOnline(relatedEvent.locationCode))
                                            ? 'bg-gray-400 cursor-not-allowed'
                                            : 'bg-purple-500 hover:bg-purple-600'
                                        } text-white px-4 py-2 text-sm transition-colors duration-200 flex items-center justify-center`}
                                      >
                                        {relatedEvent.locationCode && !isPanelOnline(relatedEvent.locationCode) ? (
                                          <>
                                            <WifiOff className="h-4 w-4 mr-1" /> Offline
                                          </>
                                        ) : (
                                          isLoading ? 'Processing...' : 'Download'
                                        )}
                                      </button>
                                    )}
                                    {isDownloadComplete(relatedEvent) && (
                                      <button
                                        disabled={true}
                                        className="bg-green-500 text-white px-4 py-2 text-sm cursor-default"
                                      >
                                        Complete
                                      </button>
                                    )}
                                    {relatedEvent.category === 'VIDEO_ALARM' && (
                                      <div className="grid grid-cols-2 gap-2">
                                        {relatedEvent.videoDetails && relatedEvent.videoDetails.previewVideo.data === 'VIDEO-CLIP GENERATED' ? (
                                          <button
                                            onClick={() => onViewVideo(relatedEvent, 'preview')}
                                            className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 text-sm transition-colors duration-200 flex items-center justify-center"
                                          >
                                            <Video className="w-4 h-4 mr-1" />
                                            Clip
                                          </button>
                                        ) : relatedEvent.videoDetails?.status === 'RECEIVING' ? (
                                          <div className="bg-gray-50 text-gray-400 px-4 py-2 text-sm flex items-center justify-center">
                                            <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                                            Clip
                                          </div>
                                        ) : null}
                                        
                                        {relatedEvent.videoDetails && relatedEvent.videoDetails.fullVideo?.data ? (
                                          <button
                                            onClick={() => onViewVideo(relatedEvent, 'full')}
                                            className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 text-sm transition-colors duration-200 flex items-center justify-center"
                                          >
                                            <Film className="w-4 h-4 mr-1" />
                                            Full
                                          </button>
                                        ) : relatedEvent.videoDetails?.status === 'RECEIVING' ? (
                                          <div className="bg-gray-50 text-gray-400 px-4 py-2 text-sm flex items-center justify-center">
                                            <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                                            Full
                                          </div>
                                        ) : null}
                                      </div>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};